import React from 'react';
import { Platform, ViewStyle } from 'react-native';
import { Box, HStack, Text } from '@gluestack-ui/themed';
import { flexRender, Table } from '@tanstack/react-table';
import { TableHeaderProps } from '../types';
import { AdminObjectName } from '../../DataColumnConfig';

/**
 * TableHeader - The sticky header row for the DataTable.
 *
 * It manages:
 * - Rendering of column titles or custom header components.
 * - Integration with TanStack Table's sorting logic (via `handleSort`).
 * - Column resizing logic for web platforms (via `handleResize`).
 * - Visual indicators for active sorting (Chevron icons).
 * - Fixed versus flexible width calculations for columns.
 *
 * @param {TableHeaderProps} props - Configuration for the table instance, column widths, and sizing constraints.
 * @returns {React.ReactElement} The rendered header row.
 */
const TableHeader = <K extends AdminObjectName>({
  table,
  columnWidths,
  tableColumns,
  minColumnWidth,
  maxColumnWidth,
  height,
  resizable,
  handleResize,
}: TableHeaderProps<K>) => (
  <HStack
    alignItems="center"
    height={height}
    pb="$2"
    borderBottomWidth={1}
    borderColor="$backgroundLight200"
    minWidth="100%"
  >
    {table && (table as Table<unknown>).getHeaderGroups().map((headerGroup) => headerGroup.headers.map((header) => {
      const { id } = header.column;
      const isSelection = id === 'select';
      const isFixed = (header.column.columnDef as { meta?: { isFixed?: boolean } }).meta?.isFixed || isSelection;
      const metaWidth = (header.column.columnDef as { meta?: { width?: number } }).meta?.width;
      const sizeFromState = columnWidths[id];

      const rawSize = isFixed
        ? metaWidth || 32
        : typeof sizeFromState === 'number'
          ? sizeFromState
          : metaWidth || header.getSize?.() || 180;

      const isBoolean = tableColumns[id]?.type === 'bool';
      const effectiveMin = isBoolean || isSelection ? 32 : minColumnWidth;
      const clamped = Math.min(Math.max(rawSize, effectiveMin), maxColumnWidth);
      const useFixed = isFixed || rawSize < 100;

      return (
        <Box
          key={header.id}
          justifyContent={isBoolean || isSelection ? 'center' : 'flex-start'}
          alignItems={isSelection ? 'center' : undefined}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            position: 'relative',
            paddingLeft: 8,
            paddingRight: isSelection ? 0 : 8,
            marginRight: isSelection ? 4 : 10,
            width: useFixed ? clamped : undefined,
            flexGrow: useFixed ? 0 : clamped,
            flexBasis: useFixed ? undefined : 0,
            flexShrink: useFixed ? 0 : 1,
          }}
        >
          {typeof header.column.columnDef.header === 'string' ? (
            <Text size="xs" fontWeight="$bold" color="$textHeading">
              {header.column.columnDef.header}
            </Text>
          ) : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}
          {Platform.OS === 'web' && resizable && !isFixed && (
            <Box
              position="absolute"
              right={-4}
              top={0}
              h="100%"
              w={12}
              // eslint-disable-next-line react-native/no-inline-styles
              style={{ cursor: 'col-resize', userSelect: 'none' } as unknown as ViewStyle}
              // @ts-expect-error - onMouseDown is a web-only prop
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleResize(id, e.clientX, rawSize, header.column);
              }}
            />
          )}
        </Box>
      );
    }))}
  </HStack>
  );

export default TableHeader;
